import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { PageLoader } from "@/shared/components/PageLoader";
import { queryClient } from "@/shared/stores/query-client";
import { Toaster } from "@/shared/ui/toaster";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { Auth0TokenProvider } from "@/widgets/auth/Auth0TokenProvider";
import { AuthCallback } from "@/widgets/auth/AuthCallback";
import { ProtectedRoute } from "@/widgets/auth/ProtectedRoute";
import { AppLayout } from "@/widgets/layout/AppLayout";
import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

const Accounts = lazy(() => import("@/pages/Accounts").then((m) => ({ default: m.Accounts })));
const Applications = lazy(() =>
  import("@/pages/Applications").then((m) => ({ default: m.Applications }))
);
const Configuration = lazy(() =>
  import("@/pages/Configuration").then((m) => ({ default: m.Configuration }))
);
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const NotFound = lazy(() => import("@/pages/NotFound").then((m) => ({ default: m.NotFound })));
const Reports = lazy(() => import("@/pages/Reports").then((m) => ({ default: m.Reports })));
const Transactions = lazy(() =>
  import("@/pages/Transactions").then((m) => ({ default: m.Transactions }))
);

export function App() {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

  return (
    <ErrorBoundary>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
        }}
        cacheLocation="localstorage"
      >
        <Auth0TokenProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <BrowserRouter>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AuthCallback />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Login />
                      </Suspense>
                    }
                  />
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      path="applications"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Applications />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="accounts"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Accounts />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="transactions"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Transactions />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="configuration"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Configuration />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      path="reports"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Reports />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                  </Route>
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </Auth0TokenProvider>
      </Auth0Provider>
    </ErrorBoundary>
  );
}
